import type { BudgetNoteRowDraft } from '../types/budgetNote';

export type BudgetMetaColKey = 'cat1' | 'cat2' | 'cat3' | 'cat4' | 'memo';

export const BUDGET_META_COL_KEYS: BudgetMetaColKey[] = ['cat1', 'cat2', 'cat3', 'cat4', 'memo'];

export type BudgetMetaColorFieldKey =
  | 'cat1_color'
  | 'cat2_color'
  | 'cat3_color'
  | 'cat4_color'
  | 'memo_color';

export const BUDGET_META_COLOR_FIELD: Record<BudgetMetaColKey, BudgetMetaColorFieldKey> = {
  cat1: 'cat1_color',
  cat2: 'cat2_color',
  cat3: 'cat3_color',
  cat4: 'cat4_color',
  memo: 'memo_color',
};

export function budgetMetaCellKey(rowId: string, colKey: BudgetMetaColKey): string {
  return `${rowId}:${colKey}`;
}

export function parseBudgetMetaCellKey(key: string): { rowId: string; colKey: BudgetMetaColKey } | null {
  const sep = key.indexOf(':');
  if (sep <= 0) return null;
  const rowId = key.slice(0, sep);
  const colKey = key.slice(sep + 1) as BudgetMetaColKey;
  if (!BUDGET_META_COL_KEYS.includes(colKey)) return null;
  return { rowId, colKey };
}

export function rectBudgetMetaCellKeys(
  orderedRows: BudgetNoteRowDraft[],
  selectableCols: BudgetMetaColKey[],
  anchor: { rowIdx: number; colKey: BudgetMetaColKey },
  current: { rowIdx: number; colKey: BudgetMetaColKey }
): string[] {
  const aCol = selectableCols.indexOf(anchor.colKey);
  const cCol = selectableCols.indexOf(current.colKey);
  if (aCol < 0 || cCol < 0) return [];
  const minR = Math.min(anchor.rowIdx, current.rowIdx);
  const maxR = Math.max(anchor.rowIdx, current.rowIdx);
  const minC = Math.min(aCol, cCol);
  const maxC = Math.max(aCol, cCol);
  const keys: string[] = [];
  for (let r = minR; r <= maxR; r += 1) {
    const row = orderedRows[r];
    if (!row) continue;
    for (let c = minC; c <= maxC; c += 1) {
      keys.push(budgetMetaCellKey(row.id, selectableCols[c]));
    }
  }
  return keys;
}

export function budgetMetaCellNeighborKeys(
  orderedRows: BudgetNoteRowDraft[],
  selectableCols: BudgetMetaColKey[],
  rowIdx: number,
  colKey: BudgetMetaColKey
): { top: string | null; bottom: string | null; left: string | null; right: string | null } {
  const row = orderedRows[rowIdx];
  if (!row) return { top: null, bottom: null, left: null, right: null };
  const colIdx = selectableCols.indexOf(colKey);
  const topRow = orderedRows[rowIdx - 1];
  const bottomRow = orderedRows[rowIdx + 1];
  return {
    top: topRow ? budgetMetaCellKey(topRow.id, colKey) : null,
    bottom: bottomRow ? budgetMetaCellKey(bottomRow.id, colKey) : null,
    left: colIdx > 0 ? budgetMetaCellKey(row.id, selectableCols[colIdx - 1]) : null,
    right:
      colIdx >= 0 && colIdx < selectableCols.length - 1
        ? budgetMetaCellKey(row.id, selectableCols[colIdx + 1])
        : null,
  };
}

export function normalizeBudgetRowMarkColors(row: BudgetNoteRowDraft): BudgetNoteRowDraft {
  return {
    ...row,
    cat1_color: (row.cat1_color ?? '').trim(),
    cat2_color: (row.cat2_color ?? '').trim(),
    cat3_color: (row.cat3_color ?? '').trim(),
    cat4_color: (row.cat4_color ?? '').trim(),
    memo_color: (row.memo_color ?? '').trim(),
  };
}
